import { getPublishedId, studioPath } from "@sanity/client/csm";
import { fallback, is, minLength, object, optional, parse, pipe, record, safeParse, string, unknown } from "valibot";
const lengthyStr$1 = pipe(string(), minLength(1)), optionalLengthyStr = optional(lengthyStr$1), sanityNodeSchema = object({
	baseUrl: lengthyStr$1,
	dataset: optionalLengthyStr,
	id: lengthyStr$1,
	path: lengthyStr$1,
	projectId: optionalLengthyStr,
	tool: optionalLengthyStr,
	type: optionalLengthyStr,
	workspace: optionalLengthyStr,
	perspective: fallback(string(), "drafts")
});
/** @internal */
function isValidSanityNode(node) {
	return is(sanityNodeSchema, node);
}
function isArray(value) {
	return value !== null && Array.isArray(value);
}
/** @internal */
function pathToUrlString(path) {
	let str = "";
	for (let segment of path) {
		if (typeof segment == "string") {
			str && (str += "."), str += segment;
			continue;
		}
		if (typeof segment == "number") {
			str && (str += ":"), str += `${segment}`;
			continue;
		}
		if (isArray(segment)) {
			str && (str += ":"), str += `${segment.join(",")}}`;
			continue;
		}
		if (segment._key) {
			str && (str += ":"), str += `${segment._key}`;
			continue;
		}
	}
	return str;
}
/**
* Transforms sanity node data into an encoded string
* @param node - An unencoded sanity node object
* @returns An encoded string of sanity data
* @public
*/
function encodeSanityNodeData(node) {
	let { id: _id, path, baseUrl, tool, workspace, type } = node;
	if (isValidSanityNode(node)) return [
		["id", getPublishedId(_id)],
		["type", type],
		["path", pathToUrlString(studioPath.fromString(path))],
		["base", encodeURIComponent(baseUrl)],
		["workspace", workspace],
		["tool", tool]
	].filter(([, value]) => !!value).map((part) => part.join("=")).join(";");
}
/**
* A helper function for creating `data-sanity` attributes by explicitly providing metadata.
* @returns An object with methods for incrementally adding and scoping metadata, and for generating a data attribute string.
* @public
*/
function createDataAttribute(props) {
	function normalizePath(path) {
		return path ? typeof path == "string" ? studioPath.fromString(path) : path : [];
	}
	function toString(props) {
		if (!props.id) throw Error("`id` is required to create a data attribute");
		if (!props.type) throw Error("`type` is required to create a data attribute");
		if (!props.path || !props.path.length) throw Error("`path` is required to create a data attribute");
		return encodeSanityNodeData({
			baseUrl: props.baseUrl || "/",
			workspace: props.workspace,
			tool: props.tool,
			type: props.type,
			id: props.id,
			path: typeof props.path == "string" ? props.path : studioPath.toString(props.path),
			perspective: props.perspective
		});
	}
	let DataAttribute = (path) => toString({
		...props,
		path: [...normalizePath(props.path), ...normalizePath(path)]
	});
	return DataAttribute.toString = function() {
		return toString(props);
	}, DataAttribute.combine = function(attrs) {
		return createDataAttribute({
			...props,
			...attrs
		});
	}, DataAttribute.scope = function(path) {
		return createDataAttribute({
			...props,
			path: [...normalizePath(props.path), ...normalizePath(path)]
		});
	}, DataAttribute;
}
const RE_SEGMENT_WITH_INDEX = /^([\w-]+):(0|[1-9][0-9]{0,8})$/, RE_SEGMENT_WITH_TUPLE = /^([\w-]+):([0-9]+),([0-9]+)$/, RE_SEGMENT_WITH_KEY = /^([\w-]+):([\w-]+)$/;
/** @internal */
function urlStringToPath(str) {
	let path = [];
	for (let segment of str.split(".")) {
		let withIndex = RE_SEGMENT_WITH_INDEX.exec(segment);
		if (withIndex) {
			path.push(withIndex[1], Number(withIndex[2]));
			continue;
		}
		let withTuple = RE_SEGMENT_WITH_TUPLE.exec(segment);
		if (withTuple) {
			path.push(withTuple[1], [Number(withTuple[2]), Number(withTuple[3])]);
			continue;
		}
		let withKey = RE_SEGMENT_WITH_KEY.exec(segment);
		if (withKey) {
			path.push(withKey[1], { _key: withKey[2] });
			continue;
		}
		path.push(segment);
	}
	return path;
}
const lengthyStr = pipe(string(), minLength(1)), sanityLegacyNodeSchema = object({
	origin: lengthyStr,
	href: lengthyStr,
	data: optional(record(string(), unknown()))
});
/**
* Transforms a sanity data string into sanity node data
* @param str - An encoded string of sanity data
* @internal
*/
function decodeSanityString(str) {
	let data = str.split(";").reduce((acc, segment) => {
		let [key, value] = segment.split("=");
		if (!key || segment.includes("=") && !value) return acc;
		switch (key) {
			case "id":
				acc.id = value;
				break;
			case "type":
				acc.type = value;
				break;
			case "path":
				acc.path = studioPath.toString(urlStringToPath(value));
				break;
			case "base":
				acc.baseUrl = decodeURIComponent(value);
				break;
			case "perspective":
				acc.perspective = value;
				break;
			case "tool":
				acc.tool = value;
				break;
			case "workspace":
				acc.workspace = value;
				break;
			case "projectId":
				acc.projectId = value;
				break;
			case "dataset": acc.dataset = value;
		}
		return acc;
	}, {});
	if (isValidSanityNode(data)) return data;
}
/**
* Transforms stringified JSON into sanity node data
* @param str - JSON sanity data
*/
function decodeSanityObject(data) {
	let sanityNode = safeParse(sanityNodeSchema, data);
	if (sanityNode.success) return sanityNode.output;
	let sanityLegacyNode = safeParse(sanityLegacyNodeSchema, data);
	if (sanityLegacyNode.success) try {
		let url = new URL(sanityLegacyNode.output.href, typeof document > "u" ? "https://example.com" : location.origin);
		return url.searchParams.size > 0 ? parse(sanityNodeSchema, Object.fromEntries(url.searchParams.entries())) : sanityLegacyNode.output;
	} catch (err) {
		return console.error("Failed to parse sanity node", err), sanityLegacyNode.output;
	}
}
/**
* Transforms sanity data from multiple formats into sanity node data
* @param str - Sanity data as a string of unknown format
* @internal
*/
function decodeSanityNodeData(data) {
	if (typeof data == "object" && data) return decodeSanityObject(data);
	try {
		return decodeSanityObject(JSON.parse(data));
	} catch {
		return decodeSanityString(data);
	}
}
export { createDataAttribute, decodeSanityNodeData, encodeSanityNodeData, pathToUrlString, urlStringToPath };

//# sourceMappingURL=index.js.map